import React, { useState } from "react";
import emailjs from "emailjs-com";

const ContactForm = () => {
  const [forxlata, setForxlata] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [status, setStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [submitCount, setSubmitCount] = useState(0);
  const [lastSubmitTime, setLastSubmitTime] = useState(0);

  // 🛡️ SÉCURITÉ : Validation et sanitisation des entrées
  const sanitizeInput = (input) => {
    return input.trim().replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  };

  const validateForm = () => {
    const { name, message } = forxlata;
    
    // Validation de base
    if (name.length < 2 || name.length > 50) {
      setStatus("Le nom doit contenir entre 2 et 50 caractères.");
      return false;
    }
    
    if (message.length < 10 || message.length > 1000) {
      setStatus("Le message doit contenir entre 10 et 1000 caractères.");
      return false;
    }
    
    // Détection de spam simple
    const spamKeywords = ['viagra', 'casino', 'lottery', 'winner', 'urgent', 'click here'];
    const messageToCheck = (name + ' ' + message).toLowerCase();
    if (spamKeywords.some(keyword => messageToCheck.includes(keyword))) {
      setStatus("Message détecté comme spam.");
      return false;
    }
    
    return true;
  };

  // 🛡️ SÉCURITÉ : Protection contre le spam (rate limiting)
  const isRateLimited = () => {
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;
    
    // Maximum 3 soumissions toutes les 5 minutes
    if (submitCount >= 3 && (now - lastSubmitTime) < fiveMinutes) {
      setStatus("Trop de tentatives. Veuillez attendre 5 minutes.");
      return true;
    }
    
    // Reset le compteur après 5 minutes
    if ((now - lastSubmitTime) > fiveMinutes) {
      setSubmitCount(0);
    }
    
    return false;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // 🛡️ SÉCURITÉ : Sanitisation des entrées
    const sanitizedValue = sanitizeInput(value);
    setForxlata({ ...forxlata, [name]: sanitizedValue });
  };

  const sendEmail = (e) => {
    e.preventDefault();
    
    // 🛡️ SÉCURITÉ : Vérifications avant envoi
    if (isRateLimited()) {
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    setStatus("Envoi en cours...");
    setIsLoading(true);

    // Vérification que les variables d'environnement sont définies
    if (!process.env.REACT_APP_EMAILJS_SERVICE_ID || 
        !process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 
        !process.env.REACT_APP_EMAILJS_PUBLIC_KEY) {
      setStatus("Erreur de configuration. Vérifiez le fichier .env");
      setIsLoading(false);
      return;
    }

    emailjs
      .send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          from_name: forxlata.name,
          from_email: forxlata.email,
          message: forxlata.message,
          to_name: "Gautier VASSE",
          reply_to: forxlata.email,
          to_email: process.env.REACT_APP_RECIPIENT_EMAIL,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      )
      .then(
        (response) => {
          setStatus("Message envoyé avec succès !");
          setForxlata({ name: "", email: "", message: "" });
          setIsLoading(false);
          
          // 🛡️ SÉCURITÉ : Mise à jour du rate limiting
          setSubmitCount(prev => prev + 1);
          setLastSubmitTime(Date.now());
        },
        (err) => {
          setStatus("Erreur lors de l'envoi du message.");
          console.error("Erreur :", err);
          setIsLoading(false);
        }
      );
  };

  return (
    <div className="w-full max-w-2xl mx-auto px-4 sm:px-6">
      <form onSubmit={sendEmail} className="border-2 border-gray-400 p-4 sm:p-6 md:p-8 lg:p-10 rounded-2xl w-full">
        <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 mt-3 sm:mt-4">
          <input
            className="border-2 border-gray-300 p-3 sm:p-4 md:p-5 lg:p-6 rounded-2xl w-full text-sm sm:text-base focus:border-purple-500 focus:outline-none transition duration-300"
            type="text"
            name="name"
            placeholder="Votre nom"
            value={forxlata.name}
            onChange={handleChange}
            required
            maxLength="50"
            minLength="2"
            autoComplete="name"
            spellCheck="false"
          />

          <input
            className="border-2 border-gray-300 p-3 sm:p-4 md:p-5 lg:p-6 rounded-2xl w-full text-sm sm:text-base focus:border-purple-500 focus:outline-none transition duration-300"
            type="email"
            name="email"
            placeholder="Votre email"
            value={forxlata.email}
            onChange={handleChange}
            required
            maxLength="100"
            autoComplete="email"
            spellCheck="false"
          />
        </div>

        <textarea
          className="border-2 border-gray-300 p-3 sm:p-4 md:p-5 rounded-2xl mt-3 sm:mt-4 w-full h-24 sm:h-28 md:h-32 text-sm sm:text-base focus:border-purple-500 focus:outline-none transition duration-300 resize-none"
          name="message"
          placeholder="Votre message"
          value={forxlata.message}
          onChange={handleChange}
          required
          maxLength="1000"
          minLength="10"
          spellCheck="true"
        />
        
        <div className="flex justify-center gap-4 mt-3 sm:mt-4">
          <button 
            className={`cursor-pointer border-2 px-6 sm:px-8 md:px-10 py-2 sm:py-3 rounded-2xl text-sm sm:text-base md:text-lg transition duration-300 font-medium ${
              isLoading 
                ? 'border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed' 
                : 'border-gray-400 hover:bg-purple-700 hover:text-white hover:border-purple-700'
            }`}
            type="submit" 
            disabled={isLoading}
          >
            {isLoading ? "Envoyer" : "Envoyer"}
          </button>
        </div>
      </form>
      
      {status && (
        <div className="mt-4 text-center">
          <p className={`text-sm sm:text-base font-medium ${status.includes('succès') ? 'text-green-600' : 'text-green-800'}`}>
            {status}
          </p>
        </div>
      )}
    </div>
  );
};

export default ContactForm;
